import React, { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Form, Col, Row, Button, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLifeRing, faTimes, faBook } from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";
import { savOperations } from "state/ducks/store/sav";
import { extractTicketId } from "./freshdeskTicket";
import { SAV_NOTICE_URL } from "./savNotice";

/**
 * Point d'entrée « depuis un ticket » : l'agent colle le ticket Freshdesk qu'il a
 * sous les yeux, le client est retrouvé par l'email du ticket, et la modale
 * « Faire un SAV » s'ouvre déjà remplie (ticket, catégorie, motif, commande).
 *
 * Sans compte correspondant, le ticket reste en attente : la modale le reprendra
 * quand l'agent aura ouvert la bonne fiche (cf. Customers.js, pendingTicketId).
 */
const TicketSavEntry = (props) => {
  const { pendingTicketId, onTicketResolved, onClearPending, disabled } = props;

  const dispatch = useDispatch();
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const ticketId = extractTicketId(value);
    if (!ticketId) {
      swal({ title: "Ticket manquant", text: "Collez le numéro ou l'URL du ticket Freshdesk.", icon: "warning" });
      return;
    }

    setIsLoading(true);
    let item = null;
    try {
      // Le middleware API résout avec la réponse axios : { data: { code, result } }.
      const res = await dispatch(savOperations.getFreshdeskTicket(ticketId));
      item = res?.data?.result?.item || null;
    } catch (err) {
      item = null;
    }
    setIsLoading(false);

    if (!item) {
      swal({ title: "Ticket introuvable", text: `Aucun ticket Freshdesk #${ticketId}.`, icon: "error" });
      return;
    }

    setValue("");
    onTicketResolved(ticketId, item);
  };

  return (
    <Form onSubmit={submit}>
      <Container>
        <Col xs={12}>
          <h4>
            Partir d'un ticket Freshdesk
            <NoticeLink href={SAV_NOTICE_URL} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faBook} /> Notice SAV
            </NoticeLink>
          </h4>
          <p>
            Collez le numéro ou l'URL du ticket : le client est retrouvé par l'email du ticket,
            et la fenêtre « Faire un SAV » s'ouvre déjà remplie.
          </p>
          <InputGroup>
            <Form.Control
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Ex. 27536 ou https://moulindev.freshdesk.com/a/tickets/27536"
              disabled={disabled || isLoading}
            />
            <InputGroup.Append>
              <Button type="submit" variant="warning" disabled={disabled || isLoading}>
                <FontAwesomeIcon icon={faLifeRing} /> {isLoading ? "Recherche…" : "Faire un SAV"}
              </Button>
            </InputGroup.Append>
          </InputGroup>
          {pendingTicketId && (
            <Pending>
              Ticket <b>#{pendingTicketId}</b> en attente : ouvrez la fiche du bon client puis
              « Faire un SAV », il sera repris automatiquement.
              <Button variant="link" size="sm" onClick={onClearPending} title="Oublier ce ticket">
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </Pending>
          )}
        </Col>
      </Container>
    </Form>
  );
};

const Container = styled(Row)`
  p {
    margin-bottom: 0.5rem;
  }
  h4 {
    color: ${(props) => props.theme.colors.green};
  }
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  margin: 0 auto;
  margin-bottom: 1rem;
  color: #444;
  background: #fff;
  border-radius: 4px;
  padding: 1rem 0rem;
`;

const NoticeLink = styled.a`
  font-size: 0.85rem;
  margin-left: 0.75rem;
  vertical-align: middle;
`;

const Pending = styled.div`
  margin-top: 0.5rem;
  padding: 0.4rem 0.6rem;
  background: #fff8e1;
  border-radius: 4px;
  font-size: 0.9rem;
`;

export default TicketSavEntry;
